import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toastr from 'toastr';
import ConfirmModal from './ConfirmModal';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const Statistics = ({ token, user }) => {
  const isAdmin = user?.role === 'admin';

  // --- Выбор пользователя (только для admin) ---
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserLogin, setSelectedUserLogin] = useState('');
  const [userPrefix, setUserPrefix] = useState('');

  // --- State для основных данных ---
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportsPage, setReportsPage] = useState(1);
  const [reportsTotalPages, setReportsTotalPages] = useState(1);
  const [reportsTotalCount, setReportsTotalCount] = useState(0);
  const [reportsUserFilter, setReportsUserFilter] = useState('');
  
  const [chartData, setChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [chartPeriod, setChartPeriod] = useState('day'); // 'day' | 'week'

  const [cityStats, setCityStats] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  // --- State для Детальной статистики (Модалка) ---
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: '', onConfirm: null, danger: false });
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailStats, setDetailStats] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [showGeoModal, setShowGeoModal] = useState(false);

  // --- State для статистики по AppName (Модалка) ---
  const [showAppNameModal, setShowAppNameModal] = useState(false);
  const [appNameStats, setAppNameStats] = useState([]);
  const [loadingAppNames, setLoadingAppNames] = useState(false);

  const [filters, setFilters] = useState({
    date: new Date().toISOString().split('T')[0],
    fromTime: '00:00',
    toTime: '23:59'
  });

  const hasActiveProcess = reports.some(r => r.status === 'processing');

  // --- Загрузка пользователей для пикера (только admin) ---
  useEffect(() => {
    if (!isAdmin) return;
    api.get('/admin/users').then(r => {
      const nonAdmins = (r.data || []).filter(u => u.role !== 'admin');
      setAllUsers(nonAdmins);
    }).catch(() => {});
  }, [isAdmin]);

  // --- ЗАГРУЗКА ---

  const fetchReports = useCallback(async (silent = false) => {
    if (!silent) setLoadingReports(true);
    try {
      let data;
      if (isAdmin) {
        const params = { page: reportsPage, limit: 20 };
        if (reportsUserFilter) params.login = reportsUserFilter;
        ({ data } = await api.get('/admin/stats/check-reports', { params }));
      } else {
        ({ data } = await api.get('/user/check-reports', { params: { page: reportsPage, limit: 20 } }));
      }
      setReports(data?.reports || []);
      setReportsTotalPages(data?.totalPages || 1);
      setReportsTotalCount(data?.totalCount || 0);
    } catch (error) {
      console.error(error);
      if (!silent) toastr.error('Ошибка загрузки истории проверок');
    } finally {
      if (!silent) setLoadingReports(false);
    }
  }, [isAdmin, reportsPage, reportsUserFilter]);

  const fetchChartData = useCallback(async (period) => {
    const p = period || chartPeriod;
    setLoadingChart(true);
    try {
      let data;
      if (p === 'week') {
        if (isAdmin) {
          const params = selectedUserLogin ? { login: selectedUserLogin } : {};
          ({ data } = await api.get('/admin/stats/stats-week', { params }));
        } else {
          ({ data } = await api.get('/user/stats-week'));
        }
      } else {
        const { date, fromTime, toTime } = filters;
        if (isAdmin) {
          const params = { date, fromTime, toTime };
          if (selectedUserLogin) params.login = selectedUserLogin;
          ({ data } = await api.get('/admin/stats/stats-history', { params }));
        } else {
          ({ data } = await api.get('/user/stats-history', { params: { date, fromTime, toTime } }));
        }
      }
      setChartData(data || []);
    } catch (error) {
      console.error(error);
      toastr.error('Ошибка загрузки графика');
    } finally {
      setLoadingChart(false);
    }
  }, [filters, isAdmin, selectedUserLogin, chartPeriod]);

  const fetchCityStats = useCallback(async () => {
    setLoadingCities(true);
    try {
      let data;
      if (isAdmin) {
        const params = selectedUserLogin ? { login: selectedUserLogin } : {};
        ({ data } = await api.get('/admin/stats/stats-cities', { params }));
      } else {
        ({ data } = await api.get('/user/stats-cities'));
      }
      setCityStats(data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingCities(false);
    }
  }, [isAdmin, selectedUserLogin]);

  const fetchDetailStats = async () => {
    setLoadingDetail(true);
    setShowDetailModal(true);
    try {
      let data;
      if (isAdmin) {
        const params = selectedUserLogin ? { login: selectedUserLogin } : {};
        ({ data } = await api.get('/admin/stats/stats-detailed', { params }));
      } else {
        ({ data } = await api.get('/user/stats-detailed'));
      }
      setDetailStats(data || []);
    } catch (error) {
      console.error(error);
      toastr.error('Ошибка загрузки детальной статистики');
      setShowDetailModal(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const fetchAppNames = async () => {
    setLoadingAppNames(true);
    setShowAppNameModal(true);
    try {
      let data;
      if (isAdmin) {
        const params = selectedUserLogin ? { login: selectedUserLogin } : {};
        ({ data } = await api.get('/admin/stats/stats-appnames', { params }));
      } else {
        ({ data } = await api.get('/user/stats-appnames'));
      }
      setAppNameStats(data || []);
    } catch (error) {
      console.error(error);
      toastr.error('Ошибка загрузки статистики по AppName');
      setShowAppNameModal(false);
    } finally {
      setLoadingAppNames(false);
    }
  };

  useEffect(() => {
    fetchReports();
    fetchChartData();
    fetchCityStats();
  }, [fetchReports, fetchChartData, fetchCityStats]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (hasActiveProcess) fetchReports(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [hasActiveProcess, fetchReports]);

  // Сброс на 1-ю страницу при смене фильтра — fetchReports вызовется через useEffect
  const handleReportsUserFilter = (login) => {
    setReportsUserFilter(login);
    setReportsPage(1);
  };

  const handleReportsPageChange = (newPage) => {
    setReportsPage(newPage);
  };

  // --- ОБРАБОТЧИКИ ---

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleInstallCheck = () => {
    if (hasActiveProcess) return toastr.warning('Дождитесь завершения текущей задачи!');
    const msg = isAdmin
      ? 'Запустить проверку APK для ВСЕХ пользователей?\nБудет создан отчёт для каждого пользователя.'
      : 'Запустить проверку установки APK?\n(Лимит: 8 часов)';
    setConfirmModal({
      isOpen: true,
      message: msg,
      onConfirm: async () => {
        setConfirmModal(p => ({ ...p, isOpen: false }));
        try {
          if (isAdmin) {
            await api.post('/admin/stats/check-install-all');
            toastr.success('Глобальная проверка APK запущена для всех пользователей.');
          } else {
            await api.post('/user/check-install-batch');
            toastr.success('Проверка APK запущена.');
          }
          fetchReports();
        } catch (error) {
          toastr.warning(error.response?.data?.message || 'Ошибка запуска');
        }
      }
    });
  };

  const handleCleanup = () => {
    if (hasActiveProcess) return toastr.warning('Дождитесь завершения текущей задачи!');
    const msg = isAdmin
      ? 'ВНИМАНИЕ!\nБудет запущена очистка (14+ дней) для ВСЕХ пользователей.\nБудут удалены все старые устройства.\n\nПродолжить?'
      : 'ВНИМАНИЕ!\nБудут УДАЛЕНЫ БЕЗВОЗВРАТНО все устройства,\nкоторые не были в сети более 14 дней.\n\nПродолжить?';
    setConfirmModal({
      isOpen: true,
      message: msg,
      danger: true,
      onConfirm: async () => {
        setConfirmModal(p => ({ ...p, isOpen: false }));
        try {
          if (isAdmin) {
            await api.post('/admin/stats/cleanup-all');
            toastr.success('Глобальная очистка (14 дней) запущена для всех пользователей.');
          } else {
            await api.post('/user/cleanup-dead-devices');
            toastr.success('Очистка запущена.');
          }
          fetchReports();
        } catch (error) {
          toastr.warning(error.response?.data?.message || 'Ошибка запуска');
        }
      }
    });
  };

  const handleCleanupNA = () => {
    setConfirmModal({
      isOpen: true,
      message: 'ВНИМАНИЕ!\nБудут удалены все N/A устройства — те, у которых нет brand/model/системной информации (не прошли инициализацию).\nЭто действие необратимо!\n\nПродолжить?',
      danger: true,
      onConfirm: async () => {
        setConfirmModal(p => ({ ...p, isOpen: false }));
        try {
          const res = await api.post('/admin/stats/cleanup-na');
          toastr.success(res.data.message || 'N/A устройства удалены.');
          fetchReports();
        } catch (error) {
          toastr.warning(error.response?.data?.message || 'Ошибка удаления N/A');
        }
      }
    });
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const renderStatus = (status) => {
    if (status === 'processing') return <span style={{color: '#3498db', fontWeight: 'bold'}}>⏳ В работе...</span>;
    if (status === 'failed') return <span style={{color: 'red'}}>Ошибка</span>;
    return <span style={{color: '#2ecc71'}}>✅ Готово</span>;
  };

  const renderType = (type) => {
    if (type === 'cleanup_14d') return <span style={{color: '#e74c3c'}}>🗑 Очистка (14д)</span>;
    if (type === 'weekly_transfer') return <span style={{color: '#fd79a8'}}>📦 Передача (Otstuki)</span>;
    if (type === 'cleanup_na') return <span style={{color: '#e17055'}}>🗑 Del N/A</span>;
    return <span style={{color: '#a29bfe'}}>🔎 Проверка APK</span>;
  };

  const renderDetails = (rep) => {
    const hasBreakdown = rep.versionBreakdown && Object.keys(rep.versionBreakdown).length > 0;
    const BreakdownBlock = () => (
      <div style={{ marginTop: '4px', fontSize: '11px', color: '#999' }}>
        <span style={{color:'#ccc'}}>Версии: </span>
        {Object.entries(rep.versionBreakdown).map(([ver, count]) => (
          <span key={ver} style={{ marginRight: '6px' }}>
            {ver.toUpperCase()}: <b style={{color: '#fff'}}>{count}</b>
          </span>
        ))}
      </div>
    );

    if (rep.type === 'weekly_transfer') {
      return (
        <div>
          <span>Найдено: <b style={{color:'#fdcb6e'}}>{rep.totalChecked || 0}</b></span>
          {' | '}
          <span>Передано: <span style={{color:'#fd79a8'}}>{rep.transferredCount || 0}</span></span>
        </div>
      );
    }

    if (rep.type === 'cleanup_na') {
      return (
        <div>
          <span>Найдено: <b style={{color:'#fdcb6e'}}>{rep.totalChecked || 0}</b></span>
          {' | '}
          <span>Удалено: <span style={{color:'#ff6b6b'}}>{rep.deletedCount || 0}</span></span>
        </div>
      );
    }

    if (rep.type === 'cleanup_14d') {
      return (
        <div>
          <span style={{ color: '#ff6b6b' }}>Удалено: {rep.deletedCount || 0}</span>
          {hasBreakdown && <BreakdownBlock />}
        </div>
      );
    }

    return (
      <div>
        <span>
          OK: <span style={{color:'#2ecc71'}}>{rep.successCount}</span> | 
          ERR: <span style={{color:'#f1c40f'}}>{rep.errorCount}</span> | 
          DEL: <span style={{color:'#ff6b6b'}}>{rep.movedToDeleted}</span>
        </span>
        {rep.movedToDeleted > 0 && hasBreakdown && <BreakdownBlock />}
      </div>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ background: 'rgba(35, 35, 43, 0.95)', border: '1px solid #555', padding: '10px', borderRadius: '5px', fontSize: '12px' }}>
          <p style={{ margin: '0 0 5px', color: '#fff' }}>⏰ {label}</p>
          {payload.map((entry, index) => (
            <div key={index} style={{ color: entry.color, marginBottom: '2px' }}>
              {entry.name}: <b>{entry.value}</b>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="statistics-page" style={{ padding: '15px', color: '#e6e9ef', maxWidth: '100%', boxSizing: 'border-box' }}>
      
      {/* 1. ЗАГОЛОВОК И КНОПКИ */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>📊 Статистика</h2>

        {/* Выбор пользователя (только admin) + поиск по префиксу */}
        {isAdmin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ color: '#aaa', fontSize: '13px' }}>Просмотр статистики:</span>
            <input
              type="text"
              placeholder="Поиск по имени..."
              value={userPrefix}
              onChange={e => {
                setUserPrefix(e.target.value);
                setSelectedUserLogin(''); // сбросить выбор при новом поиске
              }}
              style={{ ...inputStyle, minWidth: '140px' }}
            />
            <select
              value={selectedUserLogin}
              onChange={e => setSelectedUserLogin(e.target.value)}
              style={{ ...inputStyle, minWidth: '160px' }}
            >
              <option value="">— Общая (все) —</option>
              {allUsers
                .filter(u => !userPrefix || u.login.toLowerCase().startsWith(userPrefix.toLowerCase()))
                .map(u => (
                  <option key={u._id} value={u.login}>{u.login}</option>
                ))}
            </select>
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button onClick={() => { fetchReports(); fetchChartData(); fetchCityStats(); }} className="apk-check-btn" style={{ background: '#444', flex: 1 }}>🔄 Обновить</button>
          <button onClick={fetchDetailStats} className="apk-check-btn" style={{ background: '#2980b9', flex: 1 }}>📊 Детально (Версии)</button>
          <button onClick={() => { setShowGeoModal(true); fetchCityStats(); }} className="apk-check-btn" style={{ background: '#1a6b4a', flex: 1 }}>🌍 Гео</button>
          <button onClick={fetchAppNames} className="apk-check-btn" style={{ background: '#7b4f8a', flex: 1 }}>📱 По (метка+назв.)</button>
          <button onClick={handleInstallCheck} disabled={hasActiveProcess} className="apk-check-btn" style={{ background: hasActiveProcess ? '#555' : '#6c63ff', flex: 1 }}>🔎 APK Check</button>
          <button onClick={handleCleanup} disabled={hasActiveProcess} className="apk-check-btn" style={{ background: hasActiveProcess ? '#555' : '#e74c3c', flex: 1 }}>🗑 Clean 14d</button>
          {isAdmin && (
            <button onClick={handleCleanupNA} className="apk-check-btn" style={{ background: '#7f5af0', flex: 1 }}>🗑 Del N/A</button>
          )}
        </div>
      </div>

      {/* 2. ГРАФИК */}
      <div style={{ background: '#23232b', border: '1px solid #444', borderRadius: '8px', padding: '15px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <button
            onClick={() => { setChartPeriod('day'); fetchChartData('day'); }}
            style={{ ...inputStyle, cursor: 'pointer', border: 'none', fontWeight: 'bold', background: chartPeriod === 'day' ? '#6c63ff' : '#333' }}
          >По дням</button>
          <button
            onClick={() => { setChartPeriod('week'); fetchChartData('week'); }}
            style={{ ...inputStyle, cursor: 'pointer', border: 'none', fontWeight: 'bold', background: chartPeriod === 'week' ? '#6c63ff' : '#333' }}
          >Последние 7 дней</button>
        </div>
        {chartPeriod === 'day' && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px', alignItems: 'center' }}>
            <input type="date" name="date" value={filters.date} onChange={handleFilterChange} style={inputStyle} />
            <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
              <input type="time" name="fromTime" value={filters.fromTime} onChange={handleFilterChange} style={inputStyle} />
              <span style={{color:'#777'}}>-</span>
              <input type="time" name="toTime" value={filters.toTime} onChange={handleFilterChange} style={inputStyle} />
            </div>
            <button onClick={() => fetchChartData('day')} style={{ ...inputStyle, background: '#6c63ff', cursor: 'pointer', border: 'none', fontWeight: 'bold' }}>OK</button>
          </div>
        )}

        <div style={{ width: '100%', height: 280, fontSize: '12px' }}>
          {loadingChart ? (
            <div style={centerStyle}>Загрузка...</div>
          ) : chartData.length === 0 ? (
            <div style={centerStyle}>Нет данных за этот период</div>
          ) : (
            <ResponsiveContainer width="100%" height={270}>
              <AreaChart data={chartData} margin={{ top: 5, right: 0, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="gradOnline" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#82ca9d" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#444" vertical={false} />
                <XAxis dataKey="timeLabel" stroke="#888" tick={{fontSize: 10}} minTickGap={30} />
                <YAxis stroke="#888" tick={{fontSize: 10}} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ paddingTop: '10px' }} />
                <Area type="monotone" dataKey="totalDevices" name="Всего" stroke="#8884d8" fill="url(#gradTotal)" strokeWidth={2} />
                <Area type="monotone" dataKey="onlineNow" name="Онлайн" stroke="#82ca9d" fill="url(#gradOnline)" strokeWidth={2} />
                <Area type="monotone" dataKey="onlineToday24h" name="Заходили сегодня" stroke="#ffc658" fill="none" strokeWidth={2} strokeDasharray="5 5" />
                <Area type="monotone" dataKey="newDevicesToday" name="Новые" stroke="#ff7300" fill="none" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* 3. ТАБЛИЦА ОТЧЕТОВ */}
      <div style={{ background: '#23232b', border: '1px solid #444', borderRadius: '8px', overflow: 'hidden' }}>
        {/* Заголовок таблицы с фильтром по пользователю для админа */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #444', flexWrap: 'wrap', gap: '8px' }}>
          <span style={{ color: '#aaa', fontSize: '13px' }}>
            📋 Отчёты {reportsTotalCount > 0 && <span style={{ color: '#777' }}>({reportsTotalCount} всего)</span>}
          </span>
          {isAdmin && (
            <select
              value={reportsUserFilter}
              onChange={e => handleReportsUserFilter(e.target.value)}
              style={{ ...inputStyle, minWidth: '150px', fontSize: '12px', padding: '4px 8px' }}
            >
              <option value="">— Все пользователи —</option>
              <option value="system">system (авто)</option>
              {allUsers.map(u => (
                <option key={u._id} value={u.login}>{u.login}</option>
              ))}
            </select>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '500px' }}>
            <thead>
              <tr style={{ background: '#2c2c35', borderBottom: '1px solid #444' }}>
                {isAdmin && <th style={thStyle}>Пользователь</th>}
                <th style={thStyle}>Дата</th>
                <th style={thStyle}>Задача</th>
                <th style={thStyle}>Статус</th>
                <th style={thStyle}>Инфо</th>
                <th style={thStyle}>Детали</th>
              </tr>
            </thead>
            <tbody>
              {loadingReports && (
                <tr><td colSpan={isAdmin ? 6 : 5} style={{ padding: '20px', textAlign: 'center', color: '#777' }}>Загрузка...</td></tr>
              )}
              {!loadingReports && reports.length === 0 && (
                <tr><td colSpan={isAdmin ? 6 : 5} style={{ padding: '20px', textAlign: 'center', color: '#777' }}>Пусто</td></tr>
              )}
              {reports.map((rep) => (
                <tr key={rep._id} style={{ borderBottom: '1px solid #333' }}>
                  {isAdmin && <td style={{ ...tdStyle, color: '#aaa' }}>{rep.userId}</td>}
                  <td style={tdStyle}>{formatDate(rep.createdAt)}</td>
                  <td style={tdStyle}>{renderType(rep.type)}</td>
                  <td style={tdStyle}>{renderStatus(rep.status)}</td>
                  <td style={tdStyle}>
                    {`Итого: ${rep.totalChecked || 0}`}
                  </td>
                  <td style={{ ...tdStyle, fontSize: '13px' }}>
                    {renderDetails(rep)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Пагинация */}
        {reportsTotalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px', borderTop: '1px solid #333' }}>
            <button
              onClick={() => handleReportsPageChange(1)}
              disabled={reportsPage <= 1}
              style={{ padding: '4px 8px', background: reportsPage <= 1 ? '#333' : '#444', color: reportsPage <= 1 ? '#666' : '#fff', border: '1px solid #555', borderRadius: '4px', cursor: reportsPage <= 1 ? 'default' : 'pointer' }}
            >«</button>
            <button
              onClick={() => handleReportsPageChange(reportsPage - 1)}
              disabled={reportsPage <= 1}
              style={{ padding: '4px 10px', background: reportsPage <= 1 ? '#333' : '#444', color: reportsPage <= 1 ? '#666' : '#fff', border: '1px solid #555', borderRadius: '4px', cursor: reportsPage <= 1 ? 'default' : 'pointer' }}
            >‹</button>
            <span style={{ color: '#aaa', fontSize: '13px', minWidth: '80px', textAlign: 'center' }}>
              {reportsPage} / {reportsTotalPages}
            </span>
            <button
              onClick={() => handleReportsPageChange(reportsPage + 1)}
              disabled={reportsPage >= reportsTotalPages}
              style={{ padding: '4px 10px', background: reportsPage >= reportsTotalPages ? '#333' : '#444', color: reportsPage >= reportsTotalPages ? '#666' : '#fff', border: '1px solid #555', borderRadius: '4px', cursor: reportsPage >= reportsTotalPages ? 'default' : 'pointer' }}
            >›</button>
            <button
              onClick={() => handleReportsPageChange(reportsTotalPages)}
              disabled={reportsPage >= reportsTotalPages}
              style={{ padding: '4px 8px', background: reportsPage >= reportsTotalPages ? '#333' : '#444', color: reportsPage >= reportsTotalPages ? '#666' : '#fff', border: '1px solid #555', borderRadius: '4px', cursor: reportsPage >= reportsTotalPages ? 'default' : 'pointer' }}
            >»</button>
          </div>
        )}
      </div>

      {/* --- МОДАЛЬНОЕ ОКНО ДЕТАЛЬНОЙ СТАТИСТИКИ --- */}
      {showDetailModal && (
        <div style={modalBackdropStyle}>
          <div style={modalContentStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#fff' }}>
                📊 Детализация по версиям
                {isAdmin && selectedUserLogin && <span style={{ fontSize: '13px', color: '#aaa', marginLeft: '10px' }}>— {selectedUserLogin}</span>}
              </h3>
              <button onClick={() => setShowDetailModal(false)} style={closeBtnStyle}>✕</button>
            </div>

            {loadingDetail ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>Загрузка данных...</div>
            ) : (
              <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#3a3a44', color: '#ccc' }}>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Версия</th>
                      <th style={{ padding: '10px', textAlign: 'center' }}>Всего</th>
                      <th style={{ padding: '10px', textAlign: 'center', color: '#2ecc71' }}>Онлайн</th>
                      <th style={{ padding: '10px', textAlign: 'center', color: '#e74c3c' }}>Оффлайн</th>
                      <th style={{ padding: '10px', textAlign: 'center', color: '#f39c12' }}>Новые</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailStats.length === 0 ? (
                      <tr><td colSpan="5" style={{ padding: '15px', textAlign: 'center', color: '#777' }}>Устройств не найдено</td></tr>
                    ) : (
                      detailStats.map((item) => (
                        <tr key={item.version} style={{ borderBottom: '1px solid #444' }}>
                          <td style={{ padding: '10px', fontWeight: 'bold', color: '#fff' }}>{item.version.toUpperCase()}</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>{item.total}</td>
                          <td style={{ padding: '10px', textAlign: 'center', color: '#2ecc71' }}>{item.online}</td>
                          <td style={{ padding: '10px', textAlign: 'center', color: '#e74c3c' }}>{item.offline}</td>
                          <td style={{ padding: '10px', textAlign: 'center', color: '#f39c12' }}>{item.newToday > 0 ? `+${item.newToday}` : '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            
            <div style={{ marginTop: '15px', textAlign: 'right' }}>
              <button onClick={() => setShowDetailModal(false)} style={modalCloseBtnStyle}>Закрыть</button>
            </div>
          </div>
        </div>
      )}
      
      {/* --- МОДАЛЬНОЕ ОКНО ГЕО-СТАТИСТИКИ --- */}
      {showGeoModal && (
        <div style={modalBackdropStyle}>
          <div style={modalContentStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#fff' }}>
                🌍 Гео-статистика по городам
                {isAdmin && selectedUserLogin && <span style={{ fontSize: '13px', color: '#aaa', marginLeft: '10px' }}>— {selectedUserLogin}</span>}
              </h3>
              <button onClick={() => setShowGeoModal(false)} style={closeBtnStyle}>✕</button>
            </div>
            {loadingCities ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>Загрузка данных...</div>
            ) : cityStats.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#777' }}>Нет данных о городах</div>
            ) : (
              <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#3a3a44', color: '#ccc' }}>
                      <th style={{ padding: '10px', textAlign: 'left' }}>#</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Город</th>
                      <th style={{ padding: '10px', textAlign: 'center' }}>Кол-во</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cityStats.map((item, idx) => (
                      <tr key={item.city} style={{ borderBottom: '1px solid #444' }}>
                        <td style={{ padding: '10px', color: '#888', fontSize: '12px' }}>{idx + 1}</td>
                        <td style={{ padding: '10px', fontWeight: 'bold', color: '#fff' }}>{item.city}</td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <span style={{ background: '#6c63ff', color: '#fff', borderRadius: '10px', padding: '2px 10px', fontWeight: 'bold' }}>{item.count}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ marginTop: '15px', textAlign: 'right' }}>
              <button onClick={() => setShowGeoModal(false)} style={modalCloseBtnStyle}>Закрыть</button>
            </div>
          </div>
        </div>
      )}

      {/* --- МОДАЛЬНОЕ ОКНО СТАТИСТИКИ ПО APPNAME --- */}
      {showAppNameModal && (
        <div style={modalBackdropStyle}>
          <div style={modalContentStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#fff' }}>
                📱 По (метка+назв.) — новые с 00:00
                {isAdmin && selectedUserLogin && <span style={{ fontSize: '13px', color: '#aaa', marginLeft: '10px' }}>— {selectedUserLogin}</span>}
              </h3>
              <button onClick={() => setShowAppNameModal(false)} style={closeBtnStyle}>✕</button>
            </div>

            {loadingAppNames ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>Загрузка данных...</div>
            ) : appNameStats.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#777' }}>Нет данных за сегодня</div>
            ) : (
              <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#3a3a44', color: '#ccc' }}>
                      <th style={{ padding: '10px', textAlign: 'left' }}>#</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>AppName</th>
                      <th style={{ padding: '10px', textAlign: 'center' }}>Кол-во</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appNameStats.map((item, idx) => (
                      <tr key={item.appName} style={{ borderBottom: '1px solid #444' }}>
                        <td style={{ padding: '10px', color: '#888', fontSize: '12px' }}>{idx + 1}</td>
                        <td style={{ padding: '10px', fontWeight: 'bold', color: '#fff' }}>{item.appName}</td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <span style={{ background: '#7b4f8a', color: '#fff', borderRadius: '10px', padding: '2px 10px', fontWeight: 'bold' }}>{item.count}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ marginTop: '15px', textAlign: 'right' }}>
              <button onClick={() => setShowAppNameModal(false)} style={modalCloseBtnStyle}>Закрыть</button>
            </div>
          </div>
        </div>
      )}

      <p style={{ marginTop: '15px', fontSize: '12px', color: '#666' }}>
        * <b>Проверка APK:</b> Лимит 8 часов. Перемещает в "Удаленные", если APK нет и менее 10 мин офлайн.<br/>
        * <b>Очистка (14+):</b> Лимит 24 часа. ПОЛНОСТЬЮ удаляет устройства, которые не были в сети более 14 дней.<br/>
        {isAdmin && <><b>Del N/A:</b> Удаляет устройства без brand/model/системной информации (не прошедшие инициализацию).</>}
      </p>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        danger={confirmModal.danger}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(p => ({ ...p, isOpen: false }))}
      />
    </div>
  );
};

// --- СТИЛИ ---
const inputStyle = {
  background: '#1f2227', border: '1px solid #555', color: '#fff', 
  padding: '6px', borderRadius: '4px', outline: 'none', fontSize: '13px'
};
const centerStyle = { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#777' };
const thStyle = { padding: '10px', color: '#aaa', fontSize: '12px', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px', fontSize: '12px' };

const modalBackdropStyle = {
  position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.7)', zIndex: 9999,
  display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '15px'
};
const modalContentStyle = {
  backgroundColor: '#23232b', border: '1px solid #444', borderRadius: '8px',
  width: '100%', maxWidth: '500px', padding: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
  boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto'
};
const closeBtnStyle = {
  background: 'none', border: 'none', color: '#888', fontSize: '20px', cursor: 'pointer'
};
const modalCloseBtnStyle = {
  background: '#444', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer'
};

export default Statistics;
